import React, { useState } from "react";
import { Plus, Minus, Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../shadcn/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Badge } from "../../shadcn/ui/badge";
import {
  calculateSubscriptionCost,
  getSubscriptionQuantity,
  isQuantityEditable,
  getOfferLimitParts,
} from "../../utils/subscriptionUtils";
import { useServiceSubcategories } from "../../hooks/useServiceSubcategories";

const renderOfferLimit = (offer, pkg, serviceSubcategories) => {
  const { subcategoryName, period, checkIns } = getOfferLimitParts(offer, pkg, serviceSubcategories);
  return (
    <span>
      You can book all <strong className="font-bold text-gray-900">{subcategoryName}</strong> for{" "}
      <strong className="font-bold text-gray-900">{period}</strong>{" "}
      <strong className="font-bold text-gray-900">{checkIns}</strong>.
    </span>
  );
};

const PackageDetailCard = ({ pkg, currentUser, formatCurrency, isUpgrading, onSelect, accentColor = "#1d4ed8" }) => {
  const isAddOn = pkg?.recurring === false;
  const canEditQuantity = isQuantityEditable(currentUser, pkg) || isAddOn;
  const [quantity, setQuantity] = useState(() =>
    canEditQuantity
      ? isAddOn
        ? 1
        : getSubscriptionQuantity(currentUser, pkg)
      : getSubscriptionQuantity(currentUser, pkg)
  );

  const calculatedQuantity = canEditQuantity ? quantity : getSubscriptionQuantity(currentUser, pkg);
  const { perUserCost, totalCost, isFree } = calculateSubscriptionCost(pkg, calculatedQuantity);
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const handleSelect = () => onSelect(pkg, canEditQuantity ? quantity : null);

  const { serviceSubcategories } = useServiceSubcategories();

  return (
    <Card className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all shadow-sm hover:shadow-md overflow-hidden">
      <CardHeader
        className="px-6 py-6"
        style={{
          background: `linear-gradient(135deg, ${accentColor}15 0%, ${accentColor}08 100%)`,
          borderBottom: `1px solid ${accentColor}20`,
        }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 space-y-1">
            <CardTitle className="text-xl font-bold text-gray-900 leading-tight">{pkg.name}</CardTitle>
            {pkg.description && <CardDescription className="text-sm text-gray-700">{pkg.description}</CardDescription>}
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900">
              {isFree ? "FREE" : formatCurrency(totalCost, pkg.currency || pkg.currencyId)}
            </div>
            <div className="text-xs text-gray-600">{pkg.recurring ? "per month" : "one-time"}</div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-5">
        {canEditQuantity && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">{isAddOn ? "Quantity" : "Number of Users"}</h4>
            <div className="flex items-center gap-2 mb-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 flex-shrink-0"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1 || !!isUpgrading}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <Input
                type="number"
                min="1"
                value={quantity}
                onChange={e => {
                  const newQuantity = parseInt(e.target.value, 10) || 1;
                  setQuantity(Math.max(1, newQuantity));
                }}
                className="w-full text-center"
                disabled={!!isUpgrading}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 flex-shrink-0"
                onClick={() => {
                  setQuantity(quantity + 1);
                }}
                disabled={!!isUpgrading}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {pkg.maximumTeamSize && (
              <p className="text-xs text-gray-500">
                Maximum: {pkg.maximumTeamSize} {isAddOn ? "items" : "users"}
              </p>
            )}
          </div>
        )}

        {!canEditQuantity && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              {pkg.maximumTeamSize > 1 ? "Fixed team size" : `Matches your team size (${calculatedQuantity} users)`}
            </span>
          </div>
        )}

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Pricing</h4>
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
            <div className="flex justify-between items-center p-3">
              <div>
                <div className="text-sm font-medium text-gray-900">{isAddOn ? "Per Item" : "Per User"}</div>
                <div className="text-xs text-gray-500">Base price</div>
              </div>
              <div className="text-base font-semibold text-gray-900">
                {formatCurrency(perUserCost, pkg.currency || pkg.currencyId)}
              </div>
            </div>
            <div className="flex justify-between items-center p-3">
              <div>
                <div className="text-sm font-medium text-gray-900">{isAddOn ? "Quantity" : "Users"}</div>
                <div className="text-xs text-gray-500">{isAddOn ? "Items" : "Team size"}</div>
              </div>
              <div className="text-base font-semibold text-gray-900">× {calculatedQuantity}</div>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50">
              <div>
                <div className="text-sm font-bold text-gray-900">Total</div>
                <div className="text-xs text-gray-500">{pkg.recurring ? "Monthly" : "One-time"}</div>
              </div>
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(totalCost, pkg.currency || pkg.currencyId)}
              </div>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Details</h4>
          <div className="space-y-2.5 text-sm text-gray-700">
            <div className="flex items-center justify-between">
              <span>Starts</span>
              <span className="font-medium text-gray-900">Today</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Billing</span>
              <span className="font-medium text-gray-900">
                {pkg.recurring ? "Monthly (auto-renews)" : "One-time payment"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Cancellation</span>
              <span className="font-medium text-gray-900">Anytime</span>
            </div>
          </div>
        </div>

        {pkg?.offers && pkg.offers.length > 0 && (
          <div className="pt-3.5 border-t border-gray-100">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Booking Limits</h4>
            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
              {pkg.offers.map((offer, idx) => (
                <li key={offer.id || idx}>{renderOfferLimit(offer, pkg, serviceSubcategories)}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-1 border-t border-gray-200">
          <p className="text-xs text-gray-500 leading-relaxed">
            {pkg.recurring
              ? "Your subscription will start today and renew monthly. You can cancel anytime without penalties."
              : "Your add-on will start today with a one-time payment. There are no recurring charges."}
          </p>
        </div>

        <Button
          onClick={handleSelect}
          className="w-full h-12 text-sm font-medium bg-gray-900 text-white hover:bg-gray-800 rounded-lg transition-colors"
          style={{ backgroundColor: accentColor }}
          disabled={isUpgrading === pkg.id}
        >
          {isUpgrading === pkg.id ? "Processing..." : pkg.recurring ? "Upgrade" : "Add"}
        </Button>
      </CardContent>
    </Card>
  );
};

const UpgradeSubscriptionModal = ({
  open,
  onOpenChange,
  recurringPackages = [],
  addOnPackages = [],
  currentUser,
  formatCurrency,
  isUpgrading,
  onSelectRecurring,
  onSelectAddOn,
  accentColor = "#1d4ed8",
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Upgrade Your Subscription</DialogTitle>
          <DialogDescription>Choose a new plan and review the details before upgrading</DialogDescription>
        </DialogHeader>

        <div className="space-y-10">
          {recurringPackages.length > 0 && (
            <section>
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Subscription Plans</h3>
                <p className="text-gray-600 text-sm">
                  Recurring memberships that renew automatically each billing cycle.
                </p>
              </div>
              <div
                className={`grid grid-cols-1 gap-6 justify-center ${
                  recurringPackages.length === 1
                    ? "max-w-md mx-auto"
                    : recurringPackages.length === 2
                      ? "md:grid-cols-2 max-w-3xl mx-auto"
                      : "md:grid-cols-2 xl:grid-cols-3"
                }`}
              >
                {recurringPackages.map(pkg => (
                  <PackageDetailCard
                    key={pkg.id}
                    pkg={pkg}
                    currentUser={currentUser}
                    formatCurrency={formatCurrency}
                    isUpgrading={isUpgrading}
                    onSelect={(selectedPkg, quantity) => onSelectRecurring(selectedPkg, quantity)}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            </section>
          )}

          {addOnPackages.length > 0 && (
            <section>
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Add-On Packages</h3>
                <p className="text-gray-600 text-sm">
                  One-time boosts that complement your main plan without recurring charges.
                </p>
              </div>
              <div
                className={`grid grid-cols-1 gap-6 justify-center ${
                  addOnPackages.length === 1
                    ? "max-w-md mx-auto"
                    : addOnPackages.length === 2
                      ? "md:grid-cols-2 max-w-3xl mx-auto"
                      : "md:grid-cols-2 xl:grid-cols-3"
                }`}
              >
                {addOnPackages.map(pkg => (
                  <PackageDetailCard
                    key={pkg.id}
                    pkg={pkg}
                    currentUser={currentUser}
                    formatCurrency={formatCurrency}
                    isUpgrading={isUpgrading}
                    onSelect={(selectedPkg, quantity) => onSelectAddOn(selectedPkg, quantity)}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            </section>
          )}

          {recurringPackages.length === 0 && addOnPackages.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No plans available right now</h3>
              <p className="text-gray-600">There are currently no packages to upgrade or add on.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeSubscriptionModal;
